import React, { useEffect, useId, useState } from "react";

/**
 * Vector HD mark: black panel + metallic silver W + gold P (no raster scaling blur).
 */
function WillPlayMotionLogo({
  size = 32,
  className = "",
  animated = true,
  title = "",
  decorative = true
}) {
  const reactId = useId();
  const uid = reactId.replace(/:/g, "");
  const gidSilver = `wp-silver-${uid}`;
  const gidGold = `wp-gold-${uid}`;
  const fidPanel = `wp-panel-${uid}`;

  const [reduceMotion, setReduceMotion] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduceMotion(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const motionOn = animated && !reduceMotion;

  const svgClass = ["willplay-motion-logo", motionOn ? "willplay-motion-logo--animated" : "", className]
    .filter(Boolean)
    .join(" ");

  /* Tight panel: width follows WP + small even padding (no empty strip on the right). */
  const vbW = 66;
  const vbH = 42;
  const aspect = vbW / vbH;

  return (
    <svg
      className={svgClass}
      width={size * aspect}
      height={size}
      viewBox={`0 0 ${vbW} ${vbH}`}
      xmlns="http://www.w3.org/2000/svg"
      role={decorative ? "img" : undefined}
      aria-hidden={decorative ? "true" : undefined}
      aria-label={!decorative && title ? title : undefined}
      shapeRendering="geometricPrecision"
      textRendering="geometricPrecision"
    >
      {decorative ? <title>WillPlay</title> : null}
      <defs>
        <filter id={fidPanel} x="-25%" y="-25%" width="150%" height="150%">
          <feDropShadow dx="0" dy="1.8" stdDeviation="2.2" floodColor="#000000" floodOpacity="0.4" />
        </filter>
        <linearGradient id={gidSilver} x1="18" y1="6" x2="18" y2="34" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#ffffff" />
          <stop offset="0.22" stopColor="#e8eef5" />
          <stop offset="0.45" stopColor="#b8c4d4" />
          <stop offset="0.72" stopColor="#7c8ca0" />
          <stop offset="1" stopColor="#4b5563" />
        </linearGradient>
        <linearGradient id={gidGold} x1="45" y1="6" x2="45" y2="34" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#fffbeb" />
          <stop offset="0.18" stopColor="#fde68a" />
          <stop offset="0.42" stopColor="#fbbf24" />
          <stop offset="0.68" stopColor="#d97706" />
          <stop offset="1" stopColor="#78350f" />
        </linearGradient>
      </defs>

      <rect x="0" y="0" width={vbW} height={vbH} rx="0" fill="#000000" filter={`url(#${fidPanel})`} />

      <g
        fontFamily='Segoe UI, "Arial Black", "Helvetica Neue", Arial, sans-serif'
        fontWeight="900"
        fontStyle="italic"
        letterSpacing="-0.07em"
      >
        <text
          x="7"
          y="31"
          fontSize="27"
          fill={`url(#${gidSilver})`}
          style={{
            paintOrder: "stroke fill",
            stroke: "rgba(15, 23, 42, 0.42)",
            strokeWidth: "0.45px"
          }}
        >
          W
        </text>
        <text
          x="36"
          y="31"
          fontSize="27"
          fill={`url(#${gidGold})`}
          style={{
            paintOrder: "stroke fill",
            stroke: "rgba(69, 26, 3, 0.5)",
            strokeWidth: "0.4px"
          }}
        >
          P
        </text>
      </g>
    </svg>
  );
}

export default WillPlayMotionLogo;
